import '@ant-design/compatible/assets/index.css';
import {CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Col, Form, Input, Popconfirm, Row, Switch, Tooltip} from 'antd';
import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import AgeSelector from '../../components/create/AgeSelector';
import SubICBLocationSelector from '../../components/create/SubICBLocationSelector';
import DiagnosisTree from '../../components/create/DiagnosisTree';
import GenderSelector from '../../components/create/GenderSelector';
import HRGsSelector from '../../components/create/HRGsSelector';
import ProceduresSelector from '../../components/create/ProceduresSelector';
import TreatmentSelector from '../../components/create/TreatmentSelector';
import TrustSelector from '../../components/create/TrustSelector';
import EditableLabel from '../../components/EditableLabel';
import {cancelGroup, createOrUpdateGroup, deleteGroup, setActiveGroup} from './actions';
import {emptyGroup} from './reducers';
import {useAppSelector} from "../../hooks";

const ButtonGroup = Button.Group

interface Props {
    hasNotBeenSaved: boolean
}

const GroupTab = ({hasNotBeenSaved}: Props) => {

    const dispatch = useDispatch()
    const model = useAppSelector((state) => state.hru.active.group)
    const icd10tree = useAppSelector((state) => state.settings.values.ICD10TREE)!
    const procedureTree = useAppSelector((state) => state.settings.values.PROCEDURETREE)!

    const [form] = Form.useForm()

    const [cannotSave, setCannotSave] = useState<boolean>(true)

    useEffect(() => {
        setCannotSave(!isFormValid())
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        form.setFieldsValue(model)
    }, [model]) // eslint-disable-line react-hooks/exhaustive-deps

    const onFinish = (values: any) => {
        dispatch(createOrUpdateGroup(values))
        dispatch(setActiveGroup(emptyGroup(model.level)))
    }

    const getTitle = () => {
        const title = form.getFieldsValue(['title'])['title']
        if (_.isNil(title)) {
            return model.title
        } else {
            return title
        }
    }

    const isFormValid = () => {
        const values = form.getFieldsValue([
            'primaryDiagnosis',
            'secondaryDiagnosis',
            'treatments',
            'procedures',
            'trusts',
            'subICBLocations',
            'hrgs',
            'gender',
            'fromAge',
            'toAge',
        ])
        let valid = false
        _.forOwn(values, (value, key) => {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    valid = true
                    return false
                }
            } else if (value) {
                valid = true
                return false
            }
        })
        return valid
    }

    return (
        <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            initialValues={model}
            onFieldsChange={() => {
                setCannotSave(!isFormValid())
            }}
        >
            <Row>
                <Col span={24} className="group-content">
                    <Row>
                        <Col span={24}>
                            <div className="group-title-input">
                                <EditableLabel
                                    text={getTitle()}
                                    onSaveNotification={(value: string) => form.setFieldsValue({title: value})}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            <Form.Item name="id" style={{display: 'none'}} rules={[]}>
                                <Input type="text"/>
                            </Form.Item>

                            <Form.Item name="title" style={{display: 'none'}} rules={[]}>
                                <Input type="text"/>
                            </Form.Item>

                            <Form.Item hidden={true} name="level" style={{display: 'none'}} rules={[]}>
                                <Input type="number"/>
                            </Form.Item>

                            <DiagnosisTree
                                fieldId='primaryDiagnosis'
                                fieldName='Primary Diagnosis'
                                fieldPlaceHolder='Select Primary Diagnosis'
                                form={form}
                                isDisabled={false}
                                icd10tree={icd10tree}
                            />
                            <div className="secondary-diag-toggle">
                                <Form.Item name="diagnonsisOr" valuePropName="checked" label="" rules={[]}>
                                    <Tooltip title="Relationship between Primary & Secondary diagnosis.">
                                        <Switch checkedChildren="OR" unCheckedChildren="AND" size="small"/>
                                    </Tooltip>
                                </Form.Item>
                            </div>
                            <DiagnosisTree
                                fieldId='secondaryDiagnosis'
                                fieldName='Secondary Diagnosis'
                                fieldPlaceHolder='Select Secondary Diagnosis'
                                form={form}
                                isDisabled={false}
                                icd10tree={icd10tree}
                            />
                            <TreatmentSelector form={form}/>
                        </Col>
                        <Col span={8}>
                            <ProceduresSelector
                                form={form}
                                procedureTree={procedureTree}
                            />
                            <TrustSelector form={form}/>
                            <SubICBLocationSelector form={form}/>
                        </Col>
                        <Col span={8}>
                            <HRGsSelector form={form}/>
                            <GenderSelector form={form}/>
                            <AgeSelector form={form}/>
                        </Col>
                    </Row>
                    <div>
                        <ButtonGroup>
                            <Tooltip placement="bottom" title={hasNotBeenSaved ? "add" : "amend"}>
                                <Button shape="circle"
                                        icon={hasNotBeenSaved ? <PlusOutlined/> : <EditOutlined/>}
                                        size="small"
                                        type="primary"
                                        disabled={cannotSave}
                                        htmlType="submit"
                                />
                            </Tooltip>
                            <Popconfirm
                                disabled={hasNotBeenSaved}
                                title="Are you sure you want to delete this Group?"
                                onConfirm={() => dispatch(deleteGroup(model))}
                                okText="Yes"
                                cancelText="No">
                                <Tooltip placement="bottom" title="delete">
                                    <Button shape="circle"
                                            icon={<DeleteOutlined/>}
                                            size="small" danger
                                            disabled={hasNotBeenSaved}
                                    />
                                </Tooltip>
                            </Popconfirm>
                            <Tooltip placement="bottom" title="cancel">
                                {/*type="danger ant-btn-warning"*/}
                                <Button shape="circle"
                                        icon={<CloseOutlined/>}
                                        size="small"
                                        danger
                                        disabled={hasNotBeenSaved}
                                        onClick={e => dispatch(cancelGroup(model))}
                                />
                            </Tooltip>
                        </ButtonGroup>
                    </div>
                </Col>
            </Row>
        </Form>
    )

}

export default GroupTab